import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  onSnapshot,
  startAfter,
  limit,
  doc,
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QuerySnapshot,
  type Query,
  type QueryConstraint,
} from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { FirebaseError } from '@firebase/util';
import { db } from '../../config';
import {
  patternStatus,
  processingStatus,
  resultStatus,
  ocrStatus,
  PATTERN_ID_OTHER,
  FREE_OCR_PATTERN_ID,
} from '../../../../constants/enum';
import { WatchSearchItemsType, PatternResultSearchConditions } from '../../../../types/result';
import { patternFileNameSearch, patternFreeWordSearch } from '../../../fusejs';
import { filterByNeedsReview } from '../../../../utils/pattern/needsReviewFilter';
import { filterBySubPattern } from '../../../../utils/pattern/subPatternFilter';
import { needsReviewFilterType } from '../../../../constants/enum';
import { searchResults } from '../../../algolia/search';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getDisplayName } from '../../../../utils/displayName';

dayjs.extend(utc);
dayjs.extend(timezone);

export const resultsRef = collection(db, 'results');

/**
 * result一覧のソート: created_at降順 → file_index昇順 → page_number昇順
 */
const sortResultsByOrder = (results: DocumentData[]): void => {
  results.sort((a, b) => {
    const aTime = a.created_at?.toMillis?.() || a.created_at?.getTime?.() || 0;
    const bTime = b.created_at?.toMillis?.() || b.created_at?.getTime?.() || 0;
    if (aTime !== bTime) return bTime - aTime;

    const aFileIndex = a.file_index ?? Number.MAX_SAFE_INTEGER;
    const bFileIndex = b.file_index ?? Number.MAX_SAFE_INTEGER;
    if (aFileIndex !== bFileIndex) return aFileIndex - bFileIndex;

    const aPage = a.page_number ?? Number.MAX_SAFE_INTEGER;
    const bPage = b.page_number ?? Number.MAX_SAFE_INTEGER;
    return aPage - bPage;
  });
};

// 共通の型定義
export type ResultCallback = (
  results: DocumentData[],
  lastVisible: DocumentSnapshot | null,
  isLastPage: boolean,
) => void;

/**
 * 単一のresultレコードを取得
 */
export const getResult = async (id: string): Promise<DocumentData | undefined> => {
  const resultRef = doc(db, 'results', id);
  const result = await getDoc(resultRef);
  const data = result.data();
  if (!data) return undefined;

  // snake_case → camelCase変換
  return {
    id: result.id,
    ...data,
    patternId: data.pattern_id,
    originalFiles: data.original_files,
  };
};

/**
 * メール取り込み（#2965）: 指定した受信メッセージから生成された先頭のOCR結果への参照を取得する。
 * 受信一覧の「OCR結果を見る」リンクの遷移先（free/pattern・resultId）解決に用いる。
 * 添付複数・複数ページで結果が複数ある場合は、文書の先頭（file_index→page_number 昇順）を返す。
 * ※ 一覧用の sortResultsByOrder は created_at 降順が先頭キーのため、ページごとに created_at が
 *    ずれる pagePerResult では最終ページが先頭になってしまう。ここでは文書順で先頭ページを採る。
 * company_id と email_message_id の等値2条件のみのため、複合インデックスは不要。
 */
export const getFirstResultRefByEmailMessageId = async (
  companyId: string,
  emailMessageId: string,
  // pattern_id は自動仕分（company_common）由来だと仕分け完了まで未確定（null）のため string | null で返す
): Promise<{ resultId: string; patternId: string | null } | null> => {
  try {
    const q = query(
      resultsRef,
      where('company_id', '==', companyId),
      where('email_message_id', '==', emailMessageId),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const docs: DocumentData[] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // 文書順（先頭添付・先頭ページ）でソートして先頭を採る（created_at は先頭キーにしない）。
    docs.sort((a, b) => {
      const aFileIndex = (a.file_index as number | undefined) ?? Number.MAX_SAFE_INTEGER;
      const bFileIndex = (b.file_index as number | undefined) ?? Number.MAX_SAFE_INTEGER;
      if (aFileIndex !== bFileIndex) return aFileIndex - bFileIndex;
      const aPage = (a.page_number as number | undefined) ?? Number.MAX_SAFE_INTEGER;
      const bPage = (b.page_number as number | undefined) ?? Number.MAX_SAFE_INTEGER;
      return aPage - bPage;
    });
    const first = docs[0];
    return { resultId: first.id as string, patternId: (first.pattern_id as string | null) ?? null };
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
};

/**
 * resultドキュメントのスナップショットを取得（一回取得）
 */
export const getResultSnapshot = async (id: string): Promise<DocumentSnapshot> => {
  const resultRef = doc(db, 'results', id);
  return await getDoc(resultRef);
};

/**
 * 読取パターン用のクエリビルダー関数（buildQueryの読取パターン版）
 */
const buildPatternQuery = (
  patternId: string,
  status: number | null,
  watchItems?: WatchSearchItemsType,
  sortDirection: 'asc' | 'desc' = 'desc',
  requireCompletedOcr = false, // OCR完了済みのみを対象とするかのフラグ
  includeDeleted = false, // 削除済みも含めるフラグ（運営スタッフ用）
  companyId?: string, // 企業IDで絞り込み（代理ログイン時のみ対象企業のデータが見える / 検索条件と整合させる）
): Query => {
  const constraints: QueryConstraint[] = [
    ...(includeDeleted ? [] : [where('status', '==', resultStatus.valid)]),
    where('pattern_id', '==', patternId), // 読取パターンIDで絞り込み
    ...(companyId ? [where('company_id', '==', companyId)] : []),
  ];

  // 読取ステータスによる絞り込み（ユーザー指定を優先）
  if (watchItems?.ocrStatus !== undefined && watchItems?.ocrStatus !== null) {
    constraints.push(where('ocr_status', '==', watchItems.ocrStatus));
  } else if (requireCompletedOcr) {
    // ユーザーが読取ステータスを指定していない場合のみOCR完了済みで絞り込み
    constraints.push(where('ocr_status', '==', ocrStatus.completed));
  }

  // status による絞り込み（AiOcr版と同じロジック）
  if (status !== null) {
    if (status === processingStatus.draft || status === processingStatus.processing) {
      constraints.push(
        where('processing_status', 'in', [processingStatus.draft, processingStatus.processing]),
      );
    } else {
      constraints.push(where('processing_status', '==', status));
    }
  }
  const timeField = 'created_at';

  // 日付による絞り込み（orderByのフィールドと同じなので先に配置）
  if (watchItems?.startDate) {
    const sd = dayjs(watchItems.startDate);
    if (sd.isValid()) {
      constraints.push(where(timeField, '>=', sd.toDate()));
    }
  }
  if (watchItems?.endDate) {
    const ed = dayjs(watchItems.endDate).endOf('day');
    if (ed.isValid()) {
      constraints.push(where(timeField, '<=', ed.toDate()));
    }
  }

  // 担当者による絞り込み（Pattern版：user_idを使用）
  if (watchItems?.responsiblePerson) {
    constraints.push(where('user_id', '==', watchItems.responsiblePerson));
  }

  // 担当者(assignee)による絞り込み（#2642）
  if (watchItems?.assignee) {
    constraints.push(where('assignee_id', '==', watchItems.assignee));
  }

  // ソート（where句の後に配置）
  constraints.push(orderBy(timeField, sortDirection));

  return query(collection(db, 'results'), ...constraints);
};

/**
 * 読取パターン用の結果処理ヘルパー関数（processResultsの読取パターン版）
 */
const processPatternResults = async (
  querySnapshot: QuerySnapshot<DocumentData>,
  pageSize: number,
  watchItems?: WatchSearchItemsType,
): Promise<{
  results: DocumentData[];
  newLastVisible: DocumentSnapshot<DocumentData> | null;
  isLastPage: boolean;
}> => {
  const results: DocumentData[] = [];
  let newLastVisible: DocumentSnapshot<DocumentData> | null = null;
  let isLastPage = true;

  // クエリ結果が空の場合
  if (querySnapshot.empty) {
    return { results: [], newLastVisible: null, isLastPage: true };
  }

  // ページサイズ+1で取得して次ページ有無を判定
  querySnapshot.docs.forEach((doc, index) => {
    if (index < pageSize) {
      const data = doc.data();
      results.push({
        id: doc.id,
        ...data,
        // snake_case → camelCase変換
        patternId: data.pattern_id,
        originalFiles: data.original_files,
      });
      newLastVisible = doc;
    } else {
      isLastPage = false;
    }
  });

  // created_at降順 → file_index昇順 → page_number昇順 でソート
  sortResultsByOrder(results);

  // Pattern版：入力データ付加は不要（result側にuser_idとoriginal_filesがある）
  // ファイル名フィルタ（Pattern用FuseJSを使用）
  let filteredResults = results;
  if (watchItems?.fileName && watchItems.fileName.trim() !== '') {
    filteredResults = patternFileNameSearch(results, watchItems.fileName);
  }
  // 要注意フィルタ（#3262）。Firestore の複合インデックスを増やさないため
  // クライアントサイドで絞る（設計書 §6.4）
  filteredResults = filterByNeedsReview(filteredResults, watchItems?.needsReview);
  // 子パターンフィルタ（#2967）。要注意と同じくクライアントサイドで絞る
  filteredResults = filterBySubPattern(filteredResults, watchItems?.subPatternId);

  return { results: filteredResults, newLastVisible, isLastPage };
};

/**
 * PatternAiOcr一覧画面用のページネート関数（パフォーマンス最適化版）
 * - resultデータのみ返す（inputデータ結合なし）
 * - ファイル名検索は result.originalFiles.file_name で実行
 * - 検索なし時：リアルタイムで100件のみ取得
 * - 検索時：全件取得してクライアントサイド検索
 */
export const getPatternResultsOneTimePaginated = async (
  patternId: string,
  status: number | null,
  pageSize = 100, // デフォルトを100件に変更
  lastVisible: DocumentSnapshot<DocumentData> | null = null,
  callback: ResultCallback,
  watchItems?: WatchSearchItemsType,
  includeDeleted = false, // 削除済み結果も含めるフラグ（運営スタッフ用）
  companyId?: string, // 企業IDで絞り込み（代理ログイン時のみ対象企業のデータが見える）
  onError?: (error: unknown) => void, // リアルタイム取得失敗時のコールバック（loading解除・エラー表示用）
): Promise<() => void> => {
  // ファイル名検索・要注意フィルタがある場合は全件取得してクライアントサイドで絞る。
  // 要注意（#3262）は Firestore の複合インデックスを増やさないため、
  // ファイル名検索と同じこの経路に乗せる（設計書 §6.4）
  const hasFileNameSearch = watchItems?.fileName && watchItems.fileName.trim() !== '';
  const hasNeedsReviewFilter =
    watchItems?.needsReview !== undefined && watchItems.needsReview !== needsReviewFilterType.all;
  // 子パターンフィルタ（#2967）も同じ経路に乗せる
  const hasSubPatternFilter = !!watchItems?.subPatternId;

  if (hasFileNameSearch || hasNeedsReviewFilter || hasSubPatternFilter) {
    // 【検索時】全件取得してクライアントサイド検索
    const allQuery = buildPatternQuery(
      patternId,
      status,
      { ...watchItems, fileName: null }, // ファイル名検索・要注意フィルタはクライアントサイドで実行
      'desc',
      false,
      includeDeleted,
      companyId,
    );

    const executeSearch = async (): Promise<void> => {
      const querySnapshot = await getDocs(allQuery);

      if (querySnapshot.empty) {
        callback([], null, true);
        return;
      }

      // 全件をメモリ上で処理（inputデータは結合しない）
      const allResults: DocumentData[] = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // snake_case → camelCase変換
          patternId: data.pattern_id,
          originalFiles: data.original_files,
        };
      });

      // created_at降順 → file_index昇順 → page_number昇順 でソート
      sortResultsByOrder(allResults);

      // ファイル名検索を適用
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const filteredResults = filterBySubPattern(
        filterByNeedsReview(
          watchItems.fileName ? patternFileNameSearch(allResults, watchItems.fileName) : allResults,
          watchItems.needsReview,
        ),
        watchItems.subPatternId,
      );

      // ページネーション処理（メモリ上で）
      const startIndex = lastVisible
        ? filteredResults.findIndex(result => result.id === lastVisible.id) + 1
        : 0;
      const endIndex = Math.min(startIndex + pageSize, filteredResults.length);
      const pageResults = filteredResults.slice(startIndex, endIndex);

      // 次ページ有無の判定
      const isLastPage = endIndex >= filteredResults.length;

      // lastVisibleの更新（実際のDocumentSnapshotを取得）
      let newLastVisible: DocumentSnapshot<DocumentData> | null = null;
      if (pageResults.length > 0) {
        const lastResultId = pageResults[pageResults.length - 1].id;
        const lastDoc = querySnapshot.docs.find(doc => doc.id === lastResultId);
        newLastVisible = lastDoc || null;
      }

      callback(pageResults, newLastVisible, isLastPage);
    };

    executeSearch();

    // 検索時はリアルタイム更新しないので、何もしない関数を返す
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  } else {
    // 【初回ローディング・タブ切り替え時】リアルタイムで100件のみ取得
    let baseQuery = buildPatternQuery(
      patternId,
      status,
      watchItems,
      'desc',
      false,
      includeDeleted,
      companyId,
    );

    // lastVisibleがある場合はそれ以降
    if (lastVisible) {
      baseQuery = query(baseQuery, startAfter(lastVisible));
    }

    // limitを追加（検索なしの場合は指定件数+1のみ）
    baseQuery = query(baseQuery, limit(pageSize + 1));

    // onSnapshotでリアルタイムリスナー
    const unsubscribe = onSnapshot(
      baseQuery,
      async (querySnapshot: QuerySnapshot<DocumentData>) => {
        const { results, newLastVisible, isLastPage } = await processPatternResults(
          querySnapshot,
          pageSize,
          watchItems,
        );
        callback(results, newLastVisible, isLastPage);
      },
      error => {
        // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
        Sentry.captureException(error);
        // インデックス不足などでクエリが失敗した場合、callback が呼ばれず
        // 呼び出し側の loading が解除されないため、onError で通知する
        onError?.(error);
      },
    );

    return unsubscribe;
  }
};

/**
 * PatternAiOcr結果詳細画面用のナビゲーション関数（「次へ」「前へ」ボタン）
 * - リアルタイム監視なし（getDocs使用）
 * - 現在のレコードの前後1件ずつのIDを返す
 * - 検索条件がある場合は全件取得してクライアントサイド検索
 */
/**
 * ナビゲーション用クエリビルダー（sort_keyでソート）
 * sort_keyはcreated_at降順 → file_index昇順 → page_number昇順 を1フィールドで表現
 */
const buildNavigationQuery = (
  patternId: string,
  status: number | null,
  searchData?: WatchSearchItemsType,
  sortDirection: 'asc' | 'desc' = 'asc',
  companyId?: string, // 企業IDで絞り込み（一覧と整合させる）
): Query => {
  const constraints: QueryConstraint[] = [
    where('status', '==', resultStatus.valid),
    where('pattern_id', '==', patternId),
    where('ocr_status', '==', ocrStatus.completed),
    ...(companyId ? [where('company_id', '==', companyId)] : []),
  ];

  if (status !== null) {
    if (status === processingStatus.draft || status === processingStatus.processing) {
      constraints.push(
        where('processing_status', 'in', [processingStatus.draft, processingStatus.processing]),
      );
    } else {
      constraints.push(where('processing_status', '==', status));
    }
  }

  // 担当者による絞り込み
  if (searchData?.responsiblePerson) {
    constraints.push(where('user_id', '==', searchData.responsiblePerson));
  }

  // 担当者(assignee)による絞り込み（#2642）
  if (searchData?.assignee) {
    constraints.push(where('assignee_id', '==', searchData.assignee));
  }

  // sort_keyでソート（日付範囲フィルタはsort_keyと互換性がないためクライアントサイドで対応）
  constraints.push(orderBy('sort_key', sortDirection));

  return query(collection(db, 'results'), ...constraints);
};

/**
 * Algoliaでフリーワード検索を行い、ヒットしたresultをAlgoliaの順序のまま返す共通処理
 * 一覧表示・前後ナビゲーション・CSVエクスポートで同じ経路を通すことで順序・ヒット集合を一致させる
 *
 * @deprecated 新規実装では searchPatternResultsByAlgolia を使ってください。
 *   freeword 専用 API として残しています（既存呼び出し互換のため）。
 */
export const searchPatternResultsByFreeWord = async (
  patternId: string,
  freeWord: string,
  status: number | null,
  watchItems?: {
    responsiblePerson?: string | null;
    /** 担当者UID（#2642） */
    assignee?: string | null;
    startDate?: WatchSearchItemsType['startDate'];
    endDate?: WatchSearchItemsType['endDate'];
    ocrStatus?: number | null;
  },
  companyId?: string,
): Promise<DocumentData[]> => {
  const algoliaResult = await searchResults({
    query: freeWord.trim(),
    patternId,
    companyId,
    responsiblePerson: watchItems?.responsiblePerson ?? null,
    assignee: watchItems?.assignee ?? null,
    startDate: watchItems?.startDate ? watchItems.startDate.toDate() : null,
    endDate: watchItems?.endDate ? watchItems.endDate.toDate() : null,
    ocrStatus: watchItems?.ocrStatus ?? null,
    processingStatus: status ?? null,
  });
  return await getPatternResultsByIds(algoliaResult.objectIDs);
};

/**
 * 検索値の比較用に文字列を正規化する
 * - NFKC: 全角→半角、㈱→(株) 等の互換表現を統一
 * - toLowerCase: 英字の大小差を吸収
 */
const normalizeForCompare = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).normalize('NFKC').toLowerCase();
};

/**
 * result_data の指定フィールドに検索キーワードが含まれているかを判定
 * - 単一値フィールド（id="2" 等）: `result_data[fieldId].value` を確認
 * - テーブル子フィールド（id="10-2" 等）: `result_data[parentId][rowIdx][fieldId].value` のいずれかの行を確認
 *
 * Algoliaのフィールドスコープを補完するためにクライアントサイドで使用する。
 */
const fieldValueContainsKeyword = (
  resultData: Record<string, unknown> | undefined,
  fieldId: string,
  keyword: string,
): boolean => {
  if (!resultData) return false;
  const needle = normalizeForCompare(keyword);
  if (!needle) return true;

  const hyphenIndex = fieldId.indexOf('-');
  if (hyphenIndex >= 0) {
    // テーブル子フィールド: parentId と childId に分ける
    const parentId = fieldId.slice(0, hyphenIndex);
    const parentData = resultData[parentId];
    if (!Array.isArray(parentData)) return false;
    return parentData.some(row => {
      if (!row || typeof row !== 'object') return false;
      const cell = (row as Record<string, unknown>)[fieldId];
      if (cell && typeof cell === 'object' && 'value' in cell) {
        return normalizeForCompare((cell as { value?: unknown }).value).includes(needle);
      }
      return false;
    });
  }

  // 単一値フィールド
  const fieldData = resultData[fieldId];
  if (fieldData && typeof fieldData === 'object' && 'value' in fieldData) {
    return normalizeForCompare((fieldData as { value?: unknown }).value).includes(needle);
  }
  return false;
};

/**
 * Algoliaに全検索条件（フリーワード・ファイル名・OCR項目）を渡してヒットしたresultを返す
 * フィールドごとに fuzzy / exact のmatchModeを切り替えられる
 * OCR項目あいまい検索のみAlgolia結果取得後にクライアントサイドでフィールドスコープを厳密化する
 */
export const searchPatternResultsByAlgolia = async (params: {
  patternId: string;
  status: number | null;
  freeWord?: string | null;
  fileName?: string | null;
  fileNameMatchMode?: 'fuzzy' | 'exact';
  ocrItemKeywords?: Array<{ fieldId: string; keywords: string[]; matchMode: 'fuzzy' | 'exact' }>;
  responsiblePerson?: string | null;
  /** 担当者UID（#2642） */
  assignee?: string | null;
  startDate?: WatchSearchItemsType['startDate'];
  endDate?: WatchSearchItemsType['endDate'];
  ocrStatus?: number | null;
  companyId?: string;
}): Promise<DocumentData[]> => {
  const algoliaResult = await searchResults({
    query: params.freeWord?.trim() || '',
    patternId: params.patternId,
    companyId: params.companyId,
    responsiblePerson: params.responsiblePerson ?? null,
    assignee: params.assignee ?? null,
    startDate: params.startDate ? params.startDate.toDate() : null,
    endDate: params.endDate ? params.endDate.toDate() : null,
    ocrStatus: params.ocrStatus ?? null,
    processingStatus: params.status ?? null,
    fileName: params.fileName,
    fileNameMatchMode: params.fileNameMatchMode,
    ocrItemKeywords: params.ocrItemKeywords?.filter(item =>
      item.keywords.some(keyword => keyword.trim()),
    ),
  });
  const results = await getPatternResultsByIds(algoliaResult.objectIDs);

  // OCR項目あいまい検索のクライアントサイド厳密化
  // Algoliaのsearch_textは全フィールド値を連結しているため、fuzzy検索ではフィールドスコープが効かない。
  // ここで result_data の該当フィールド値にキーワードが含まれるかを再判定し、他フィールド由来の誤ヒットを除外する。
  const fuzzyOcrItems =
    params.ocrItemKeywords?.filter(
      item => item.matchMode === 'fuzzy' && item.keywords.some(keyword => keyword.trim()),
    ) ?? [];
  if (fuzzyOcrItems.length === 0) {
    return results;
  }
  // 項目間は AND、同一項目内の複数キーワードは OR で判定する
  return results.filter(result =>
    fuzzyOcrItems.every(item =>
      item.keywords
        .filter(keyword => keyword.trim())
        .some(keyword =>
          fieldValueContainsKeyword(
            (result as { result_data?: Record<string, unknown> }).result_data,
            item.fieldId,
            keyword,
          ),
        ),
    ),
  );
};

export const getPatternResultsPaginatedNonRealtime = async (
  patternId: string,
  status: number | null,
  currentDocSnap: DocumentSnapshot | null = null,
  searchData?: WatchSearchItemsType,
  companyId?: string, // 企業IDで絞り込み（一覧と整合させる）
): Promise<{
  nextResultId: string | null;
  prevResultId: string | null;
}> => {
  if (!currentDocSnap) {
    return { nextResultId: null, prevResultId: null };
  }

  // 検索条件の有無をチェック（ファイル名・フリーワード・日付はクライアントサイド検索が必要）
  const hasFileNameSearch = searchData?.fileName && searchData.fileName.trim() !== '';
  const hasFreeWordSearch = searchData?.freeWord && searchData.freeWord.trim() !== '';
  const hasDateSearch = searchData?.startDate || searchData?.endDate;
  const hasClientSideSearch = hasFileNameSearch || hasFreeWordSearch || hasDateSearch;

  if (!hasClientSideSearch) {
    // sort_keyを使った効率的な前後1件ずつ取得
    const currentSortKey = currentDocSnap.data()?.sort_key;

    if (currentSortKey) {
      // sort_keyがある場合: sort_keyでstartAfterして前後を取得
      const nextNavQuery = buildNavigationQuery(patternId, status, searchData, 'asc', companyId);
      const nextQueryWithLimit = query(nextNavQuery, startAfter(currentSortKey), limit(1));

      const prevNavQuery = buildNavigationQuery(patternId, status, searchData, 'desc', companyId);
      const prevQueryWithLimit = query(prevNavQuery, startAfter(currentSortKey), limit(1));

      const [nextSnapshot, prevSnapshot] = await Promise.all([
        getDocs(nextQueryWithLimit),
        getDocs(prevQueryWithLimit),
      ]);

      const nextResultId = nextSnapshot.docs.length > 0 ? nextSnapshot.docs[0].id : null;
      const prevResultId = prevSnapshot.docs.length > 0 ? prevSnapshot.docs[0].id : null;

      return { nextResultId, prevResultId };
    }

    // sort_keyがない場合（既存データ）: 従来のcreated_atベースのstartAfter
    const nextQuery = buildPatternQuery(
      patternId,
      status,
      searchData,
      'desc',
      true,
      false,
      companyId,
    );
    const nextQueryWithLimit = query(nextQuery, startAfter(currentDocSnap), limit(1));

    const prevQuery = buildPatternQuery(
      patternId,
      status,
      searchData,
      'asc',
      true,
      false,
      companyId,
    );
    const prevQueryWithLimit = query(prevQuery, startAfter(currentDocSnap), limit(1));

    const [nextSnapshot, prevSnapshot] = await Promise.all([
      getDocs(nextQueryWithLimit),
      getDocs(prevQueryWithLimit),
    ]);

    const nextResultId = nextSnapshot.docs.length > 0 ? nextSnapshot.docs[0].id : null;
    const prevResultId = prevSnapshot.docs.length > 0 ? prevSnapshot.docs[0].id : null;

    return { nextResultId, prevResultId };
  }

  // フリーワード検索がある場合は Algolia で絞り込み、一覧と同じ順序で前後を決定する
  if (hasFreeWordSearch && searchData?.freeWord) {
    try {
      let algoliaResults = await searchPatternResultsByFreeWord(
        patternId,
        searchData.freeWord,
        status,
        {
          responsiblePerson: searchData.responsiblePerson,
          assignee: searchData.assignee,
          startDate: searchData.startDate,
          endDate: searchData.endDate,
          ocrStatus: searchData.ocrStatus,
        },
        companyId,
      );
      if (hasFileNameSearch && searchData.fileName) {
        algoliaResults = patternFileNameSearch(algoliaResults, searchData.fileName);
      }
      const currentIdx = algoliaResults.findIndex(r => r.id === currentDocSnap.id);
      if (currentIdx === -1) {
        return { nextResultId: null, prevResultId: null };
      }
      const nextResultId =
        currentIdx < algoliaResults.length - 1 ? algoliaResults[currentIdx + 1].id : null;
      const prevResultId = currentIdx > 0 ? algoliaResults[currentIdx - 1].id : null;
      return { nextResultId, prevResultId };
    } catch {
      // Algolia障害時は下記の Firestore + Fuse.js フォールバックに進む
    }
  }

  // 検索条件がある場合は全件取得してクライアントサイドで検索・ソート（OCR完了済みのみ）
  const allQuery = buildPatternQuery(
    patternId,
    status,
    {
      fileName: null,
      responsiblePerson: searchData?.responsiblePerson ?? null,
      assignee: searchData?.assignee ?? null,
      startDate: searchData?.startDate ?? null,
      endDate: searchData?.endDate ?? null,
      freeWord: null,
      ocrStatus: searchData?.ocrStatus ?? null,
    },
    'desc',
    true,
    false,
    companyId,
  );
  const allSnapshot = await getDocs(allQuery);

  if (allSnapshot.empty) {
    return { nextResultId: null, prevResultId: null };
  }

  const allResults: DocumentData[] = allSnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      patternId: data.pattern_id,
      originalFiles: data.original_files,
    };
  });

  // sort_keyでソート（一覧画面と同じ順序を保証）
  sortResultsByOrder(allResults);

  // クライアントサイド検索（ファイル名・フリーワード）
  let filteredResults = allResults;
  if (searchData?.fileName && searchData.fileName.trim() !== '') {
    filteredResults = patternFileNameSearch(filteredResults, searchData.fileName);
  }
  if (searchData?.freeWord && searchData.freeWord.trim() !== '') {
    filteredResults = patternFreeWordSearch(filteredResults, searchData.freeWord);
  }

  const currentIndex = filteredResults.findIndex(result => result.id === currentDocSnap.id);

  if (currentIndex === -1) {
    return { nextResultId: null, prevResultId: null };
  }

  const nextResultId =
    currentIndex < filteredResults.length - 1 ? filteredResults[currentIndex + 1].id : null;
  const prevResultId = currentIndex > 0 ? filteredResults[currentIndex - 1].id : null;

  return { nextResultId, prevResultId };
};

/**
 * 指定ユーザーの月次resultカウント（リアルタイム監視）
 */
export const getCurrentMonthResultCountByUserIdRealTime = async (
  userId: string,
  callback: (count: number) => void,
): Promise<() => void> => {
  return new Promise(resolve => {
    const timeZone = 'Asia/Tokyo';
    const now = dayjs().tz(timeZone);
    const startOfMonth = now.startOf('month');
    const startTimestamp = Timestamp.fromDate(startOfMonth.toDate());
    const q = query(
      resultsRef,
      where('user_id', '==', userId),
      where('created_at', '>=', startTimestamp),
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot: QuerySnapshot) => {
        const count = querySnapshot.docs.filter(d => {
          const docData = d.data();
          return docData.ocr_status !== ocrStatus.error;
        }).length;
        callback(count);
      },
      error => {
        // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
        Sentry.captureException(error);
      },
    );

    resolve(unsubscribe);
  });
};

/**
 * 読取パターン用ファイル出力データを取得
 * - 指定された読取パターンID、処理ステータス、OCR完了済みの条件でデータを取得
 * - 検索条件とソート順序を適用（一覧表示と同じ順序・絞り込み）
 */
export const getPatternResultsForExport = async (
  patternId: string,
  status: number,
  watchItems?: WatchSearchItemsType | PatternResultSearchConditions,
  companyId?: string, // 企業IDで絞り込み（代理ログイン時のみ対象企業のデータが対象）
): Promise<DocumentData[]> => {
  const convertedWatchItems: WatchSearchItemsType | undefined = watchItems
    ? {
        fileName: watchItems.fileName,
        freeWord: watchItems.freeWord,
        responsiblePerson: watchItems.responsiblePerson,
        assignee: watchItems.assignee,
        startDate: watchItems.startDate,
        endDate: watchItems.endDate,
        ocrStatus: watchItems.ocrStatus,
      }
    : undefined;

  const hasFreeWord = !!convertedWatchItems?.freeWord && convertedWatchItems.freeWord.trim() !== '';

  // フリーワードがある場合は Algolia 経由で取得（一覧と同じ経路）
  if (hasFreeWord && convertedWatchItems?.freeWord) {
    try {
      let results = await searchPatternResultsByFreeWord(
        patternId,
        convertedWatchItems.freeWord,
        status,
        {
          responsiblePerson: convertedWatchItems.responsiblePerson,
          assignee: convertedWatchItems.assignee,
          startDate: convertedWatchItems.startDate,
          endDate: convertedWatchItems.endDate,
          ocrStatus: convertedWatchItems.ocrStatus,
        },
      );
      if (convertedWatchItems.fileName && convertedWatchItems.fileName.trim() !== '') {
        results = patternFileNameSearch(results, convertedWatchItems.fileName);
      }
      return results;
    } catch {
      // Algolia障害時は Firestore + Fuse.js のフォールバック経路へ
    }
  }

  // フリーワードなし、または Algolia 失敗時: Firestore クエリで取得
  const patternResultsQuery = buildPatternQuery(
    patternId,
    status,
    convertedWatchItems,
    'desc', // 一覧表示と同じ降順ソート
    false,
    false,
    companyId,
  );

  const querySnapshot = await getDocs(patternResultsQuery);
  let results: DocumentData[] = querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      // snake_case → camelCase変換
      patternId: data.pattern_id,
      originalFiles: data.original_files,
    };
  });

  if (convertedWatchItems?.fileName && convertedWatchItems.fileName.trim() !== '') {
    results = patternFileNameSearch(results, convertedWatchItems.fileName);
  }

  // フォールバック時のみ Fuse.js によるフリーワード絞り込みを実施
  if (hasFreeWord && convertedWatchItems?.freeWord) {
    results = patternFreeWordSearch(results, convertedWatchItems.freeWord);
  }

  return results;
};

/**
 * ユーザーまたは会社の最新結果30件をリアルタイム取得（ホーム画面用）
 * - user_idまたはcompany_idでフィルタ（どちらか一方を指定）
 * - created_at降順で30件
 * - 読取パターン名とユーザー情報を含める
 *
 * 代理モード（companyId指定時）の場合:
 * - company_idフィールドで直接フィルタ（新しいデータ向け）
 * - 加えて、対象企業に所属するユーザーのuser_idでもフィルタ（過去データ向け）
 */
export const getUserRecentResults = async (
  userId: string,
  callback: (results: DocumentData[]) => void,
  companyId?: string,
  onError?: (error: unknown) => void,
): Promise<() => void> => {
  // 代理モード（userId が空で companyId のみ指定）の場合は企業のユーザー一覧を取得してフィルタ
  let companyUserIds: string[] = [];
  const isProxyMode = !userId && !!companyId;
  if (isProxyMode) {
    const usersRef = collection(db, 'users');
    const usersQuery = query(usersRef, where('company_id', '==', companyId));
    const usersSnapshot = await getDocs(usersQuery);
    companyUserIds = usersSnapshot.docs.map(doc => doc.id);
  }

  // フィルタ条件を決定
  // 代理モード時は company_id でフィルタ（新データ向け）
  // 通常モード時は user_id + company_id でフィルタ（Firestoreルール評価のため）
  let q;
  if (isProxyMode) {
    q = query(
      resultsRef,
      where('company_id', '==', companyId),
      where('status', '==', resultStatus.valid),
      orderBy('created_at', 'desc'),
      limit(30),
    );
  } else {
    const conditions = [
      where('user_id', '==', userId),
      where('status', '==', resultStatus.valid),
      orderBy('created_at', 'desc'),
      limit(30),
    ];
    if (companyId) {
      conditions.unshift(where('company_id', '==', companyId));
    }
    q = query(resultsRef, ...conditions);
  }

  // 結果を処理してコールバックを呼ぶ共通関数
  const processResults = async (
    querySnapshot: QuerySnapshot<DocumentData>,
    additionalResults?: DocumentData[],
  ): Promise<void> => {
    let results: DocumentData[] = querySnapshot.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        patternId: data.pattern_id,
        originalFiles: data.original_files,
      };
    });

    // 追加結果があればマージして重複を除去
    if (additionalResults && additionalResults.length > 0) {
      const existingIds = new Set(results.map(r => r.id));
      const uniqueAdditional = additionalResults.filter(r => !existingIds.has(r.id));
      results = [...results, ...uniqueAdditional];
    }

    // created_at降順 → file_index昇順 → page_number昇順 でソート
    sortResultsByOrder(results);
    results = results.slice(0, 30);

    // 読取パターンIDを収集（重複を除く）
    // 'other' と 'free_ocr' は Firestore の patterns に実在しない仮想IDのため getDoc 対象から除外する（#2858）。
    // 一般ユーザーが存在しない patterns/free_ocr を get するとルール評価が破綻し permission-denied になる。
    const patternIds = Array.from(new Set(results.map(r => r.patternId).filter(Boolean))).filter(
      id => id !== PATTERN_ID_OTHER && id !== FREE_OCR_PATTERN_ID,
    );

    // 読取パターン情報を一括取得
    // 共有範囲（share_scope）外のパターンは get ルールで permission-denied になるため（#2804）、
    // 個別に try/catch して該当パターンはスキップする。1件でも例外を投げると processResults 全体が
    // 中断し callback に到達せず、ホーム画面のローディングが止まらなくなるため、ここで握りつぶす。
    const patternMap = new Map<string, string>();
    const deletedPatternIds = new Set<string>();
    await Promise.all(
      patternIds.map(async patternId => {
        try {
          const patternDoc = await getDoc(doc(db, 'patterns', patternId as string));
          if (patternDoc.exists()) {
            const patternData = patternDoc.data();
            if (patternData.status === patternStatus.deleted) {
              deletedPatternIds.add(patternId as string);
            } else {
              patternMap.set(patternId as string, patternData.pattern_name || '-');
            }
          }
        } catch (error) {
          // 共有範囲外のパターンは閲覧不可（pattern_name は '-' フォールバック）
          if (error instanceof FirebaseError && error.code === 'permission-denied') {
            return;
          }
          throw error;
        }
      }),
    );

    // 削除済みパターンに紐づく結果を除外
    results = results.filter(r => !r.patternId || !deletedPatternIds.has(r.patternId));

    // 結果に読取パターン名、ユーザー情報を追加
    const enrichedResults = results.map(result => ({
      ...result,
      pattern_name: result.patternId ? patternMap.get(result.patternId) || '-' : '-',
      showName: getDisplayName(result.user_name, result.is_proxy) || '-',
      iconUrl: result.user_icon_url || '',
    }));

    callback(enrichedResults);
  };

  // 代理モードの場合: company_id クエリ + user_id クエリを両方実行
  if (companyId && companyUserIds.length > 0) {
    const userIdsToQuery = companyUserIds.slice(0, 30);
    const userIdQuery = query(
      resultsRef,
      where('user_id', 'in', userIdsToQuery),
      where('status', '==', resultStatus.valid),
      orderBy('created_at', 'desc'),
      limit(30),
    );

    // 両方のクエリ結果を保持（リアルタイム更新対応）
    let companyIdResults: DocumentData[] = [];
    let userIdResults: DocumentData[] = [];

    // 結果をマージして処理する共通関数
    const mergeAndProcess = async (): Promise<void> => {
      // 両方の結果をマージして重複を除去
      const allResults = [...companyIdResults];
      const existingIds = new Set(allResults.map(r => r.id));
      for (const result of userIdResults) {
        if (!existingIds.has(result.id)) {
          allResults.push(result);
        }
      }

      // created_at降順 → file_index昇順 → page_number昇順 でソート
      sortResultsByOrder(allResults);
      const limitedResults = allResults.slice(0, 30);

      // 読取パターン情報を取得
      // 'other' と 'free_ocr' は patterns に実在しない仮想IDのため getDoc 対象から除外する（#2858）。
      const patternIds = Array.from(
        new Set(limitedResults.map(r => r.patternId).filter(Boolean)),
      ).filter(id => id !== PATTERN_ID_OTHER && id !== FREE_OCR_PATTERN_ID);
      const patternMap = new Map<string, string>();
      const deletedPatternIds = new Set<string>();
      await Promise.all(
        patternIds.map(async patternId => {
          try {
            const patternDoc = await getDoc(doc(db, 'patterns', patternId as string));
            if (patternDoc.exists()) {
              const patternData = patternDoc.data();
              if (patternData.status === patternStatus.deleted) {
                deletedPatternIds.add(patternId as string);
              } else {
                patternMap.set(patternId as string, patternData.pattern_name || '-');
              }
            }
          } catch (error) {
            // 共有範囲外のパターンは閲覧不可（pattern_name は '-' フォールバック）（#2804）
            if (error instanceof FirebaseError && error.code === 'permission-denied') {
              return;
            }
            throw error;
          }
        }),
      );

      // 削除済みパターンに紐づく結果を除外
      const filteredResults = limitedResults.filter(
        r => !r.patternId || !deletedPatternIds.has(r.patternId),
      );

      // 結果を加工してコールバック
      const enrichedResults = filteredResults.map(result => ({
        ...result,
        pattern_name: result.patternId ? patternMap.get(result.patternId) || '-' : '-',
        showName: getDisplayName(result.user_name, result.is_proxy) || '-',
        iconUrl: result.user_icon_url || '',
      }));

      callback(enrichedResults);
    };

    const onSnapshotError = (error: Error): void => {
      // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        return;
      }
      Sentry.captureException(error);
      // 呼び出し側でローディング解除・エラー表示を行えるよう通知する
      onError?.(error);
    };

    // company_id クエリのリスナー
    const unsubscribeCompanyId = onSnapshot(
      q,
      async snapshot => {
        companyIdResults = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            patternId: data.pattern_id,
            originalFiles: data.original_files,
          };
        });
        try {
          await mergeAndProcess();
        } catch (error) {
          if (error instanceof FirebaseError && error.code === 'permission-denied') {
            return;
          }
          Sentry.captureException(error);
          onError?.(error);
        }
      },
      onSnapshotError,
    );

    // user_id クエリのリスナー
    const unsubscribeUserId = onSnapshot(
      userIdQuery,
      async snapshot => {
        userIdResults = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            patternId: data.pattern_id,
            originalFiles: data.original_files,
          };
        });
        try {
          await mergeAndProcess();
        } catch (error) {
          if (error instanceof FirebaseError && error.code === 'permission-denied') {
            return;
          }
          Sentry.captureException(error);
          onError?.(error);
        }
      },
      onSnapshotError,
    );

    return () => {
      unsubscribeCompanyId();
      unsubscribeUserId();
    };
  }

  // 通常モード: user_id のみでクエリ
  const unsubscribe = onSnapshot(
    q,
    async snapshot => {
      try {
        await processResults(snapshot);
      } catch (error) {
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
        Sentry.captureException(error);
        // 呼び出し側でローディング解除・エラー表示を行えるよう通知する
        onError?.(error);
      }
    },
    error => {
      // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        return;
      }
      Sentry.captureException(error);
      // 呼び出し側でローディング解除・エラー表示を行えるよう通知する
      onError?.(error);
    },
  );

  return unsubscribe;
};

/**
 * 読取パターン用の処理ステータス別件数取得（リアルタイム監視）
 * - 指定された読取パターンIDと処理ステータスの件数を取得
 * - アップロード時点でカウント（ocr_statusは問わない）
 * - リアルタイムで件数の変化を監視
 */
export const getPatternResultsCountByStatus = async (
  patternId: string,
  status: number,
  callback: (count: number) => void,
  companyId?: string,
): Promise<() => void> => {
  const constraints: QueryConstraint[] = [
    where('status', '==', resultStatus.valid),
    where('pattern_id', '==', patternId),
  ];

  if (companyId) {
    constraints.push(where('company_id', '==', companyId));
  }

  // processing_statusによる絞り込み
  if (status === processingStatus.draft || status === processingStatus.processing) {
    constraints.push(
      where('processing_status', 'in', [processingStatus.draft, processingStatus.processing]),
    );
  } else {
    constraints.push(where('processing_status', '==', status));
  }

  const q = query(collection(db, 'results'), ...constraints);

  const unsubscribe = onSnapshot(
    q,
    (querySnapshot: QuerySnapshot) => {
      const count = querySnapshot.size;
      callback(count);
    },
    error => {
      // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        return;
      }
      Sentry.captureException(error);
    },
  );

  return unsubscribe;
};

/**
 * 指定されたドキュメントIDリストでresultsコレクションからデータを取得
 * Algolia検索結果のobjectIDリストを使用する
 * Firestoreの `in` クエリは最大30件なので、バッチに分割して取得
 */
export const getPatternResultsByIds = async (ids: string[]): Promise<DocumentData[]> => {
  if (ids.length === 0) return [];

  const batchSize = 30;
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += batchSize) {
    batches.push(ids.slice(i, i + batchSize));
  }

  const allResults: DocumentData[] = [];

  for (const batch of batches) {
    const batchQuery = query(collection(db, 'results'), where('__name__', 'in', batch));
    const querySnapshot = await getDocs(batchQuery);
    const batchResults = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        patternId: data.pattern_id,
        originalFiles: data.original_files,
      };
    });
    allResults.push(...batchResults);
  }

  // Algoliaの検索結果順序を維持する
  const idOrder = new Map(ids.map((id, index) => [id, index]));
  allResults.sort((a, b) => (idOrder.get(a.id) ?? 0) - (idOrder.get(b.id) ?? 0));

  return allResults;
};
